import React from "react";
import { motion } from "framer-motion";
import { Link2, ScanSearch, MessageCircle, Rocket, Lock, Zap, MessageSquareText, Sparkles } from "lucide-react";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

// Four editorial steps. Numbers are big, icons are small – reads like a
// Stripe/Notion how-it-works page rather than a marketing block.
// `main` icon = the step itself, `benefit` icon = the little badge chip.
const STEPS = [
    { id: "connect",  main: Link2,         benefit: Lock,              key: "hiw_step1" },
    { id: "discover", main: ScanSearch,    benefit: Zap,               key: "hiw_step2" },
    { id: "chat",     main: MessageCircle, benefit: MessageSquareText, key: "hiw_step3" },
    { id: "grow",     main: Rocket,        benefit: Sparkles,          key: "hiw_step4" },
];

const HowItWorks = () => {
    const { language } = useApp();

    return (
        <section
            id="how-it-works"
            className="w-full py-16 md:py-24 bg-[hsl(var(--card))] border-t border-[hsl(var(--border))]"
            data-testid="landing-how-it-works"
        >
            <div className="max-w-6xl mx-auto px-5 sm:px-8 md:px-12">
                {/* Section head */}
                <div className="text-center mb-12 md:mb-16">
                    <div className="text-[10px] md:text-xs font-bold tracking-[0.28em] text-[hsl(var(--muted-foreground))] uppercase">
                        {t(language, "hiw_eyebrow")}
                    </div>
                    <h2 className="mt-3 font-display font-extrabold text-[hsl(var(--primary))] text-3xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05]">
                        {t(language, "hiw_title")}
                    </h2>
                    <p className="mt-4 max-w-2xl mx-auto md:mx-0 text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
                        {t(language, "hiw_subtitle")}
                    </p>
                </div>

                {/* Steps grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                    {STEPS.map((s, i) => {
                        const MainIcon = s.main;
                        const BenefitIcon = s.benefit;
                        return (
                            <motion.div
                                key={s.id}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.5, delay: i * 0.08, ease: "easeOut" }}
                                className="relative rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-6 md:p-7 hover:border-[hsl(var(--primary))]/40 hover:shadow-lg transition-all group flex flex-col"
                                data-testid={`hiw-step-${i + 1}`}
                            >
                                {/* Big number */}
                                <div className="flex items-start justify-between">
                                    <span className="font-display font-black text-5xl md:text-6xl text-[hsl(var(--primary))]/10 group-hover:text-[hsl(var(--primary))]/20 transition-colors leading-none">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div className="w-11 h-11 rounded-2xl grid place-items-center bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]">
                                        <MainIcon className="w-5 h-5" />
                                    </div>
                                </div>

                                <h3 className="mt-6 font-display font-bold text-lg md:text-xl text-[hsl(var(--foreground))] tracking-tight">
                                    {t(language, `${s.key}_title`)}
                                </h3>
                                <p className="mt-2 text-sm md:text-[15px] leading-relaxed text-[hsl(var(--muted-foreground))]">
                                    {t(language, `${s.key}_desc`)}
                                </p>

                                {/* Benefit chip — SVG icon per PRD (no emoji) */}
                                <div className="mt-auto pt-5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[hsl(var(--primary))]">
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--secondary))] px-3 py-1.5">
                                        <BenefitIcon className="w-3.5 h-3.5" />
                                        {t(language, `${s.key}_benefit`)}
                                    </span>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default HowItWorks;
